import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,Skeleton,Stack,Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useMutation,useQuery } from '@tanstack/react-query';
import MUIDataTable,{ MUIDataTableOptions } from 'mui-datatables';
import { useEffect,useMemo,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoInformationCircleOutline } from 'react-icons/io5';
import { RiDeleteBin5Line,RiPencilFill } from 'react-icons/ri';
import { toast } from 'react-toastify';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import ServiceTypes,{ types } from '../../service/ServiceTypes';
import queryClient from '../../service/query';
import { service } from '../../types/services/types';
import ServiceInfoModal from '../PlacesComp/Modal/ServiceInfoModal';
import { StyledDiv } from '../TableHelpers/TableStyle';
import { getMuiTheme,tableOptions } from '../TableHelpers/options';
import ModalServiceType from './Modals/ModalServiceType';


export default function ServicesManagement() {
  const serviceType = useMemo(() => new ServiceTypes(), []);
  const [openModal, setOpenModal] = useState(false);
  const { t } = useTranslation('translation');
  const [typeData, setTypeData] = useState<service | null>(null);
  const [tipo, setTipo] = useState<types | null>(null);
  const [serviceToDelete, setServiceToDelete] = useState<service | null>(null);

  const { data, isLoading, isFetching, isError, isSuccess } = useQuery({
    queryKey: ['GET_SERVICES'],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error('Token not found');
        const body = { ...token, empresa: token.userEmpresa };
        const result = await serviceType.getAllByCompany(body);
        if (!result) throw new Error('Error fetching services');
        return result;
      } catch (err) {
        console.error(err);
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always'
  });

  useEffect(() => {
    if (isSuccess) trackEventMatomo('gestão de serviços', 'sucesso', 'carregamento', 'tipos de serviço carregados');
    if (isError) trackEventMatomo('gestão de serviços', 'erro', 'carregamento', 'falha ao carregar tipos de serviço');
  }, [isError, isSuccess]);

  const { mutate, isPending } = useMutation({
    mutationKey: ['REMOVE_SERVICE_TYPE'],
    mutationFn: async (value: string) => {
      const token = await AttToken();
      if (!token) throw new Error('Token not found');
      const body = { ...token, empresa: token.userEmpresa, tipoId: value };
      const promise = toast.promise(async () => {
        const res = await serviceType.deleteServiceType(body);
        if (!res || (typeof res === 'object' && 'error' in res && res.error)) {
          throw new Error('Error removing service type');
        }
        return res;
      }, {
        pending: `${t('Services.servicesManagement.removingServiceType')}`,
        success: `${t('Services.servicesManagement.serviceRemoved')}`,
        error: `${t('Services.servicesManagement.errorRemovingService')}`,
      }, { toastId: 'removeServiceType' });
      return promise;
    },
    onSuccess: () => {
      trackEventMatomo('gestão de serviços', 'sucesso', 'exclusão', 'tipo de serviço removido');
      queryClient.resetQueries({ queryKey: ['GET_SERVICES'] });
    },
    onError: () => trackEventMatomo('gestão de serviços', 'erro', 'exclusão', 'falha ao remover tipo de serviço'),
  });

  const onLoading = isLoading || isFetching || isPending;

  const columns = [
    {
      name: "nome",
      label: `${t('Services.servicesManagement.name')}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "descricao",
      label: `${t('Services.servicesManagement.description')}`,
      options: {
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "tipoId",
      label: t('edit'),
      options: {
        customBodyRender: (value: string) => {
          return (
            <Button
              endIcon={<RiPencilFill />}
              className="button"
              onClick={() => {
                trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
                const selected = data?.filter((e: service) => e.tipoId === value)[0];
                setTypeData({
                  tipoId: selected.tipoId,
                  nome: selected.nome,
                  descricao: selected.descricao,
                  dataCriacao: selected.dataCriacao
                });
                setOpenModal(true);
              }}>
              {t('edit')}
            </Button>
          );
        },
        filter: true,
        sort: false,
      }
    },
    {
      name: "tipoId",
      label: t('delete'),
      options: {
        customBodyRender: (value: string, _tableMeta: { rowIndex: number }) => (
          <Button
            endIcon={<RiDeleteBin5Line />}
            type='button'
            className="button is-danger"
            onClick={() => {
              trackEventMatomo('tela serviços', 'click', 'button', 'deleta serviço');
              setServiceToDelete(data?.find((service: service) => service.tipoId === value) ?? null);
            }}
            disabled={isPending}
          >
            {t('delete')}
          </Button>
        ),
        sort: false,
        filter: false
      }
    },
    {
      name: "tipoId",
      label: t('info'),
      options: {
        customBodyRender: (value: string) => (
          <button
            type='button'
            className="button is-small is-info"
            onClick={() => {
              trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
              const service = data?.find((e: service) => e.tipoId === value);
              setTipo(service || null);
            }}
          >
            <IoInformationCircleOutline size={30} />
          </button>
        ),
        sort: false,
        filter: false
      }
    }
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  if (isError) {
    return <span>Erro ao buscar dados</span>;
  }

  return (
    <>
      {openModal
        && <ModalServiceType
          open={openModal}
          setOpen={setOpenModal}
          typeData={typeData as service}
          setTypeData={setTypeData}
        />}
      {tipo && (
        <ServiceInfoModal
          tipo={tipo}
          setTipo={setTipo}
        />
      )}
      <Dialog open={Boolean(serviceToDelete)} onClose={() => !isPending && setServiceToDelete(null)}>
        <DialogTitle>Excluir serviço</DialogTitle>
        <DialogContent>
          <Typography>Deseja realmente excluir o serviço “{serviceToDelete?.nome}”?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceToDelete(null)} disabled={isPending}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            disabled={isPending}
            onClick={() => {
              if (!serviceToDelete?.tipoId) return;
              mutate(serviceToDelete.tipoId, { onSuccess: () => setServiceToDelete(null) });
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      <Stack
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        <Button
          disabled={onLoading}
          endIcon={<AddCircleIcon />}
          onClick={() => {
            trackEventMatomo('tela serviços', 'click', 'button', 'abre modal');
            setOpenModal(true);
          }}
          className='button is-success'>
          {`${t('notifications.ticket.criarTipoServico')}`}
        </Button>
        <Stack
          justifyContent="flex-start"
          alignItems="stretch"
          width={'100%'}
        >
          {onLoading
            ? <Skeleton variant='rounded' width={'100%'} height={400} />
            : <StyledDiv>
              <ThemeProvider theme={getMuiTheme()}>
                <MUIDataTable
                  title={`${t('Services.servicesManagement.kind')}`}
                  data={data ?? []}
                  columns={columns}
                  options={{ ...tableOptions, ...translatedTextLabels }}
                />
              </ThemeProvider>
            </StyledDiv>
          }
        </Stack>
      </Stack>
    </>
  );
}
